/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import { transparentize } from 'polished'
import React from 'react'

import Icon from '@/components/Icon'
import { mq } from '@/design-lib/media-queries'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { isEnter } from '@/utils/keyboard-helper'

import {
  geoFilterModalChildrenWrapper,
  geoFilterModalItem,
  geoFilterModalItemLabel,
  geoFilterModalItemLabelColor,
  geoFilterModalZoneCount,
} from './styles.css'

type Props = {
  activeChildrenCount?: number
  collapseControl?: string
  count?: number
  dataTestId?: string
  hasDivider?: boolean
  isAccordionDropdownToggle?: boolean
  isActive: boolean
  isOpen?: boolean
  kind?: 'area' | 'city' | 'zone' | 'other'
  label: string
  onClick: () => void
}

const Item: React.FC<Props> = ({
  activeChildrenCount,
  children,
  collapseControl,
  count,
  dataTestId,
  hasDivider = true,
  isAccordionDropdownToggle,
  isActive,
  isOpen = false,
  kind,
  label,
  onClick,
}) => {
  const { t } = useTranslation()

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  /** When the Item is the control (dropdown toggle) of the accordion it does not have
   *  the same active state styles as its children */
  const showActiveState = !isAccordionDropdownToggle && isActive

  /**
   * this function is to avoid a double ternary
   * @param theme : Theme has to be passed inside a css
   * @returns theme.colors : the color to pass to label and textCount css
   */
  const getLabelColor = (theme) => {
    if (isAccordionDropdownToggle) {
      return theme.colors.greyscale600
    }
    if (count > 0 || kind !== 'zone') {
      return theme.colors.greyscale600
    }
    return theme.colors.greyscale500
  }

  const hasDarkerLabel: boolean = isAccordionDropdownToggle || count > 0 || kind !== 'zone'

  return (
    <li>
      <div
        className={isActive ? geoFilterModalItem.active : geoFilterModalItem.default}
        data-testid={dataTestId}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => isEnter(e) && onClick()}
      >
        <div
          className={cn(
            showActiveState ? geoFilterModalItemLabel.active : geoFilterModalItemLabel.default,
            hasDarkerLabel ? geoFilterModalItemLabelColor.darker : geoFilterModalItemLabelColor.default
          )}
        >
          {label}
        </div>

        <div
          className={cn(
            showActiveState ? geoFilterModalItemLabel.active : geoFilterModalItemLabel.default,
            hasDarkerLabel ? geoFilterModalItemLabelColor.darker : geoFilterModalItemLabelColor.default
          )}
        >
          {count}
        </div>

        <Icon.Check
          className={sprinkles({
            marginLeft: 'l',
            visibility: showActiveState ? 'visible' : 'hidden',
          })}
        />

        {(activeChildrenCount > 0 || (isActive && children)) && (
          <div className={geoFilterModalZoneCount}>
            {activeChildrenCount > 0 ? (
              <span>{t('zones:zones_count.zones', { count: activeChildrenCount })}</span>
            ) : (
              <span>{t('zones:zones_count.all')}</span>
            )}
          </div>
        )}

        {children && (
          <div
            className={children ? geoFilterModalChildrenWrapper.withChildren : geoFilterModalChildrenWrapper.default}
          >
            <span
              aria-controls={collapseControl}
              aria-expanded={isOpen}
              aria-pressed={isOpen}
              className={sprinkles({
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              })}
            >
              <img
                alt=""
                className={sprinkles({ transform: isOpen ? 'rotate(180deg)' : 'none' })}
                src={`${imageBasePath}/images/arrow-down-dark.svg`}
              />
            </span>
          </div>
        )}
      </div>
      {children && (
        <ul
          data-testid={`geofilter-modal-accordion-${label.toLowerCase().replace(/\s/g, '')}`}
          id={`${label}-zones-collapse`}
          role="region"
          style={{
            display: isOpen ? 'block' : 'none',
          }}
        >
          {children}
        </ul>
      )}
      {hasDivider && <hr />}
    </li>
  )
}
export default Item
